import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[var(--hk-red)] transition-colors";
const labelCls = "block text-[10px] tracking-[0.25em] text-white/50 mb-2";

const FIELDS = [
  { key: "overline", label: "SMALL LABEL (RED)", required: true, placeholder: "WELCOME DISCOUNT" },
  { key: "title", label: "HEADLINE (WHITE)", required: true, placeholder: "15 DAYS" },
  { key: "highlight", label: "HEADLINE HIGHLIGHT (RED)", placeholder: "€100" },
  { key: "subtitle", label: "SUBLINE", placeholder: "Doubles Matches Everyday · AVR Odds 2.80" },
  { key: "text", label: "SMALL TEXT", placeholder: "Limited welcome offer for new members. Tap to claim." },
  { key: "cta", label: "BUTTON TEXT", placeholder: "CLAIM" },
];

const WelcomeOfferEditor = () => {
  const { authHeaders } = useAuth();
  const { t } = useLang();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/settings/welcome`)
      .then(({ data }) => setForm(data))
      .catch((e) => console.error(e));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.put(`${API}/admin/settings/welcome`, form, { headers: authHeaders() });
      setForm(data);
      toast.success(t.admin.saved);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  if (!form) return null;

  return (
    <form onSubmit={save} data-testid="welcome-form" className="border border-white/10 bg-[var(--hk-surface)] p-6 md:p-8 mb-10">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] tracking-[0.35em] text-[var(--hk-red)] font-bold mb-2">// WELCOME OFFER</div>
          <p className="text-xs text-white/50">
            The popup shown at the bottom-right of the homepage. Hide it or bring it back anytime — saving re-shows it to visitors who closed it.
          </p>
        </div>
        <div className="flex border border-white/15 shrink-0" role="group" aria-label="Display">
          <button
            type="button"
            onClick={() => setForm({ ...form, enabled: true })}
            data-testid="welcome-enable-btn"
            className={`flex items-center gap-2 px-4 py-2 text-[10px] font-bold tracking-[0.2em] transition-colors ${
              form.enabled ? "bg-[#10B981] text-black" : "text-white/50 hover:text-white"
            }`}
          >
            <Eye size={12} /> SHOWN
          </button>
          <button
            type="button"
            onClick={() => setForm({ ...form, enabled: false })}
            data-testid="welcome-disable-btn"
            className={`flex items-center gap-2 px-4 py-2 text-[10px] font-bold tracking-[0.2em] transition-colors ${
              !form.enabled ? "bg-[var(--hk-red)] text-white" : "text-white/50 hover:text-white"
            }`}
          >
            <EyeOff size={12} /> HIDDEN
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {FIELDS.map((f) => (
          <div key={f.key}>
            <label className={labelCls}>{f.label}</label>
            <input
              value={form[f.key] ?? ""}
              onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
              required={f.required}
              placeholder={f.placeholder}
              data-testid={`welcome-${f.key}`}
              className={inputCls}
            />
          </div>
        ))}
      </div>

      <button type="submit" disabled={busy} data-testid="welcome-save-btn" className="btn-red px-8 py-3 text-[11px] font-bold tracking-[0.3em]">
        {busy ? "..." : t.admin.save}
      </button>
    </form>
  );
};

export default WelcomeOfferEditor;
